import React, { DOMAttributes, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { FocusableElement } from '@react-types/shared'
import { mergeProps } from 'react-aria'
import { DateFieldState } from 'react-stately'
import { Box, Flex } from '../../../core-components'
import { screenReaderOnlyCss } from '../../../core-components/screen-reader-only/ScreenReaderOnly'
import { DateSegmentComponent } from '../base-calendar/DateField'

type Props = {
  fieldProps: DOMAttributes<FocusableElement>
  isDisabled?: boolean
  onOpenChange: (open: boolean) => void
  onSegmentToFocusChange: (segment: HTMLElement) => void
  popoverRef: React.RefObject<HTMLDivElement | null>
  state: DateFieldState
  variant: 'regular' | 'nested' | 'ghost'
}

export const TimeField = ({
  state,
  onSegmentToFocusChange,
  popoverRef,
  onOpenChange,
  isDisabled,
  fieldProps,
  variant,
}: Props) => {
  const nonEmptySegments = state.segments.filter(s => s.placeholder !== '')
  const ref = useRef<HTMLDivElement>(null)
  const firstEmptySegmentIndex = nonEmptySegments.findIndex(s => s.isPlaceholder)
  const firstEmptySegment =
    firstEmptySegmentIndex === -1
      ? undefined
      : (ref.current?.children[firstEmptySegmentIndex] as HTMLElement)
  useEffect(() => {
    if (firstEmptySegment) {
      onSegmentToFocusChange(firstEmptySegment)
    } else {
      onSegmentToFocusChange(ref.current?.children[0] as HTMLElement)
    }
  }, [firstEmptySegment, onSegmentToFocusChange])

  const guardedOpen = () => {
    if (!isDisabled) {
      onOpenChange(true)
    }
  }

  return (
    <div
      {...mergeProps(
        {
          onClickCapture: (e: React.MouseEvent<HTMLDivElement>) => {
            guardedOpen()
            if (!isDisabled && 'focus' in e.target && typeof e.target.focus === 'function') {
              e.target.focus()
            }
          },
          onFocus: (e: React.FocusEvent<HTMLDivElement>) => {
            if (e.relatedTarget) {
              guardedOpen()
            }
          },
          onBlur: (e: React.FocusEvent<HTMLDivElement>) => {
            if (e.relatedTarget && !popoverRef.current?.contains(e.relatedTarget)) {
              onOpenChange(false)
            }
          },
        },
        fieldProps
      )}
    >
      <StyledFlexContainer onClick={e => e.stopPropagation()}>
        <StyledValueBox ref={ref}>
          {nonEmptySegments.map((segment, i) => (
            <DateSegmentComponent
              key={i}
              variant={variant}
              segment={segment}
              state={state}
              onClose={() => {
                onOpenChange(false)
              }}
            />
          ))}
        </StyledValueBox>
      </StyledFlexContainer>
    </div>
  )
}
const StyledFlexContainer = styled(Flex)``
// This doesn't work in storybook because of this issue https://github.com/chromaui/storybook-addon-pseudo-states/issues/31
const StyledValueBox = styled(Box)`
  ${StyledFlexContainer}:not(:focus-within) {
    ${screenReaderOnlyCss}
  }
`
